// src/Components/layout/DashboardLayout.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header  from "./Header";

// ── Shared Contexts ──────────────────────────────────────────────────────────

export const TimeRangeContext = createContext({
  timeRange: "30d",
  setTimeRange: () => {},
});
export const useTimeRange = () => useContext(TimeRangeContext);

const DEFAULT_NOTIF_PREFS = {
  systemAlerts:       true,
  databaseUpdates:    true,
  performanceReports: false,
  securityAlerts:     true,
};
export const NotifPrefsContext = createContext({
  prefs:     DEFAULT_NOTIF_PREFS,
  setPrefs:  () => {},
  togglePref: () => {},
});
export const useNotifPrefs = () => useContext(NotifPrefsContext);

// ── Main Layout ──────────────────────────────────────────────────────────────
export default function DashboardLayout() {

  // ── Time range (persisted) ─────────────────────────────────────────────
  const [timeRange, setTimeRangeState] = useState(
    () => localStorage.getItem("beetroot_timerange") || "30d"
  );
  const setTimeRange = useCallback((v) => {
    setTimeRangeState(v);
    localStorage.setItem("beetroot_timerange", v);
  }, []);

  // ── Notification prefs (persisted) ────────────────────────────────────
  const [notifPrefs, setNotifPrefsState] = useState(() => {
    try {
      const s = localStorage.getItem("beetroot_notif_prefs");
      return s ? JSON.parse(s) : DEFAULT_NOTIF_PREFS;
    } catch { return DEFAULT_NOTIF_PREFS; }
  });

  const setPrefs = useCallback((newPrefs) => {
    setNotifPrefsState(newPrefs);
    localStorage.setItem("beetroot_notif_prefs", JSON.stringify(newPrefs));
  }, []);

  const togglePref = useCallback((key) => {
    setNotifPrefsState(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem("beetroot_notif_prefs", JSON.stringify(next));
      return next;
    });
  }, []);

  const TIME_RANGE_OPTIONS = [
    { value: "7d",  label: "7 days"    },
    { value: "30d", label: "30 days"   },
    { value: "90d", label: "90 days"   },
    { value: "1y",  label: "This year" },
    { value: "all", label: "All Time"  },
  ];

  return (
    <TimeRangeContext.Provider value={{ timeRange, setTimeRange }}>
      <NotifPrefsContext.Provider value={{ prefs: notifPrefs, setPrefs, togglePref }}>
        <div className="flex h-screen bg-gray-100 dark:bg-gray-900 overflow-hidden">
          <Sidebar />

          <div className="flex-1 flex flex-col overflow-hidden min-w-0">
            <Header />

            {/* ── Time Range Bar ── */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-2 flex items-center justify-end gap-1.5 flex-shrink-0">
              <span className="text-xs text-gray-400 mr-2">Time range:</span>
              {TIME_RANGE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setTimeRange(opt.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    timeRange === opt.value
                      ? "bg-red-600 text-white shadow-sm scale-105"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* ── Page Content ── */}
            <main className="flex-1 overflow-y-auto p-4 md:p-6">
              <Outlet />
            </main>
          </div>
        </div>
      </NotifPrefsContext.Provider>
    </TimeRangeContext.Provider>
  );
}